import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useShoppingCart } from '@/contexts';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { IconHelpCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import styles from './styles.module.css';
import { useStripePromise } from './use-stripe-promise';

export const CheckOutProductPage = () => {
	const { totalItemsInCart } = useShoppingCart();
	const { stripePromise, clientSecret, loading, error } = useStripePromise();
	const navigate = useNavigate();
	const backToShop = useCallback(() => navigate({ to: '/shop' }), [navigate]);
	const goToHelp = useCallback(() => navigate({ to: '/help' }), [navigate]);

	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={goToHelp}>
				Help
			</CompactButton>
		),
		[goToHelp]
	);

	const backToShopButton = useMemo(
		() => <StandardButton onClick={backToShop}>Back to Shop</StandardButton>,
		[backToShop]
	);

	return (
		<PrimaryLayout
			footer={{ children: backToShopButton }}
			header={{ backTo: '/shop', title: 'Checkout', children: helpButton }}
		>
			<ContentPanel width="xl">
				<CustomContentSection
					location={ContentSectionLocation.CheckoutProduct}
					placement={ContentSectionPlacement.Top}
				/>
				<ContentWithLoad
					loading={loading}
					error={!!error}
					isEmpty={totalItemsInCart === 0}
					isEmptyMessage="Your cart is empty"
					isErrorMessage="An Error occurred"
				>
					{!!stripePromise && !!clientSecret && (
						<EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
							<EmbeddedCheckout className={styles.stripeForm} />
						</EmbeddedCheckoutProvider>
					)}
				</ContentWithLoad>
				<CustomContentSection
					location={ContentSectionLocation.CheckoutProduct}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.CheckoutProduct}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};
